import React, { useCallback, useRef } from "react";

// Two-handle threshold bar splitting 0..100% into three decision bands:
//   red = DENY (< deny)   amber = REVIEW (deny..allow)   green = ALLOW (>= allow)
// Drag either handle (or use the keyboard) — `onChange(deny, allow)` gets fractions 0..1.
export default function ThresholdBar({ deny, allow, onChange, labels = {}, disabled = false }) {
  const barRef = useRef(null);
  const dragging = useRef(null);   // "deny" | "allow"

  const d = Math.round((Number(deny) || 0) * 100);
  const a = Math.round((Number(allow) || 0) * 100);

  const pctFromEvent = useCallback((clientX) => {
    const el = barRef.current;
    if (!el) return 0;
    const r = el.getBoundingClientRect();
    return Math.max(0, Math.min(100, Math.round(((clientX - r.left) / r.width) * 100)));
  }, []);

  const apply = useCallback((which, pct) => {
    // The handles cannot cross: deny stays <= allow.
    const nd = which === "deny" ? Math.min(pct, a) : d;
    const na = which === "allow" ? Math.max(pct, d) : a;
    onChange(nd / 100, na / 100);
  }, [d, a, onChange]);

  const onPointerDown = (which) => (e) => {
    if (disabled) return;
    e.preventDefault();
    dragging.current = which;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    apply(which, pctFromEvent(e.clientX));
  };
  const onPointerMove = (e) => {
    if (!dragging.current || disabled) return;
    apply(dragging.current, pctFromEvent(e.clientX));
  };
  const endDrag = () => { dragging.current = null; };

  const onKey = (which) => (e) => {
    if (disabled) return;
    const cur = which === "deny" ? d : a;
    const step = e.shiftKey ? 10 : 1;
    if (e.key === "ArrowLeft") { e.preventDefault(); apply(which, cur - step); }
    if (e.key === "ArrowRight") { e.preventDefault(); apply(which, cur + step); }
  };

  const Handle = ({ which, pct, tone }) => (
    <div role="slider" tabIndex={disabled ? -1 : 0} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
      aria-label={which === "deny" ? (labels.deny || "Deny") : (labels.allow || "Allow")}
      onPointerDown={onPointerDown(which)} onKeyDown={onKey(which)}
      style={{ left: `${pct}%` }}
      className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-7 w-3.5 rounded-full border-2 bg-background shadow-md
        ${disabled ? "opacity-50" : "cursor-ew-resize hover:scale-110"} transition-transform
        focus:outline-none focus:ring-2 focus:ring-primary/50 ${tone}`} />
  );

  return (
    <div className="select-none" onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerLeave={endDrag}>
      <div ref={barRef} className="relative h-8 rounded-lg overflow-hidden border border-border flex">
        <div className="bg-red-500/70 h-full" style={{ width: `${d}%` }} />
        <div className="bg-amber-400/70 h-full" style={{ width: `${Math.max(0, a - d)}%` }} />
        <div className="bg-emerald-500/70 h-full flex-1" />
      </div>
      <div className="relative h-0">
        <Handle which="deny" pct={d} tone="border-red-500" />
        <Handle which="allow" pct={a} tone="border-emerald-500" />
      </div>
      <div className="flex justify-between mt-3 text-xs">
        <span className="text-red-500 font-medium">{labels.deny || "Deny"} &lt; {d}%</span>
        <span className="text-amber-500 font-medium">{labels.review || "Review"} {d}–{a}%</span>
        <span className="text-emerald-500 font-medium">{labels.allow || "Allow"} ≥ {a}%</span>
      </div>
    </div>
  );
}
